import type { CompanyProfileData } from "@/services/api-client";
import {
  canonicalDeclaredCertificate,
  DECLARED_CERTIFICATE_OTHER_CODE,
  INDUSTRY_SECTOR_OTHER_CODE,
  isIndustrySectorCode,
} from "@/constants/company-form-options";

export interface CompanyProfileFormData {
  business_activity: string;
  company_name: string;
  legal_form: string;
  legal_form_other: string;
  industry_sector: string[];
  industry_sector_other: string;
  nip: string;
  /** pirx-lus53: declared certificate schemes (multi-select). */
  declared_certificate: string[];
  /** pirx-waua: free-text when declared_certificate includes "other". */
  declared_certificate_other: string;
}

export const emptyProfile: CompanyProfileFormData = {
  business_activity: "",
  company_name: "",
  legal_form: "",
  legal_form_other: "",
  industry_sector: [],
  industry_sector_other: "",
  nip: "",
  declared_certificate: [],
  declared_certificate_other: "",
};

export function normalizeIndustrySectorFromApi(
  value: CompanyProfileData["industry_sector"] | undefined,
  other: CompanyProfileData["industry_sector_other"] | undefined,
): { industry_sector: string[]; industry_sector_other: string } {
  if (Array.isArray(value)) {
    const cleaned = [...new Set(value.filter((entry): entry is string =>
      typeof entry === "string" && entry.trim().length > 0
    ).map((entry) => entry.trim()))];
    return {
      industry_sector: cleaned,
      industry_sector_other: other ?? "",
    };
  }

  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) {
    return { industry_sector: [], industry_sector_other: other ?? "" };
  }

  if (isIndustrySectorCode(trimmed)) {
    return {
      industry_sector: [trimmed],
      industry_sector_other: other ?? "",
    };
  }

  return {
    industry_sector: [INDUSTRY_SECTOR_OTHER_CODE],
    industry_sector_other: other?.trim() || trimmed,
  };
}

/**
 * Normalize the API `declared_certificate` (multi-cert `string[]`, legacy
 * scalar, or absent) into the multi-select form shape. Known qualifying
 * schemes resolve to their canonical option value (checked chip); any
 * free-text / unrecognized value (or a stored `declared_certificate_other`)
 * routes to the "other" entry with the free-text preserved — so a legacy
 * single-string certificate loads as exactly one entry with no data loss.
 */
export function normalizeDeclaredCertificateFromApi(
  value: CompanyProfileData["declared_certificate"] | undefined,
  other: CompanyProfileData["declared_certificate_other"] | undefined,
): { declared_certificate: string[]; declared_certificate_other: string } {
  const list = Array.isArray(value)
    ? value
    : (typeof value === "string" ? [value] : []);
  const cleaned = [
    ...new Set(
      list
        .filter((entry): entry is string =>
          typeof entry === "string" && entry.trim().length > 0
        )
        .map((entry) => entry.trim()),
    ),
  ];

  const selected: string[] = [];
  const unknowns: string[] = [];
  let sawOtherSentinel = false;
  for (const entry of cleaned) {
    if (entry.toLowerCase() === DECLARED_CERTIFICATE_OTHER_CODE) {
      sawOtherSentinel = true;
      continue;
    }
    const canonical = canonicalDeclaredCertificate(entry);
    if (canonical) {
      if (!selected.includes(canonical)) selected.push(canonical);
    } else {
      unknowns.push(entry);
    }
  }

  const otherText = (other ?? "").trim() || unknowns[0] || "";
  const needsOther = unknowns.length > 0 || sawOtherSentinel ||
    (other ?? "").trim().length > 0;
  if (needsOther) selected.push(DECLARED_CERTIFICATE_OTHER_CODE);

  return {
    declared_certificate: selected,
    declared_certificate_other: needsOther ? otherText : "",
  };
}

export function apiToFormData(
  companyData: CompanyProfileData | null | undefined,
): CompanyProfileFormData {
  const normalized = normalizeIndustrySectorFromApi(
    companyData?.industry_sector,
    companyData?.industry_sector_other,
  );
  const cert = normalizeDeclaredCertificateFromApi(
    companyData?.declared_certificate,
    companyData?.declared_certificate_other,
  );
  return {
    business_activity: companyData?.business_activity ?? "",
    company_name: companyData?.company_name ?? "",
    legal_form: companyData?.legal_form ?? "",
    legal_form_other: companyData?.legal_form_other ?? "",
    industry_sector: normalized.industry_sector,
    industry_sector_other: normalized.industry_sector_other,
    nip: companyData?.nip ?? "",
    declared_certificate: cert.declared_certificate,
    declared_certificate_other: cert.declared_certificate_other,
  };
}
